/**
 * Quotation hooks — TanStack Query wrappers for the quotation API.
 *
 * Hooks:
 * - useQuotations(filters?)      — list quotations with optional filters
 * - useQuotation(id)             — get single quotation by ID
 * - useCreateQuotation()         — mutation to create a new quotation
 * - useAdvanceQuotationStatus()  — mutation to advance status
 * - useConvertQuotation()        — mutation to convert accepted quotation to Client + Account
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '@/lib/api'
import type { QuotationRow, CreateQuotationDTO, QuotationListFilters } from '@1klick/types'
import type { QuotationStatus } from '@1klick/types'

// ── Query keys ──────────────────────────────────────────────────────────────

const quotationKeys = {
  all: ['quotations'] as const,
  list: (filters?: QuotationListFilters) => [...quotationKeys.all, 'list', filters] as const,
  detail: (id: string) => [...quotationKeys.all, 'detail', id] as const,
}

// ── Response types ──────────────────────────────────────────────────────────

interface QuotationsResponse {
  quotations: QuotationRow[]
}

interface ConversionData {
  client: { id: string; name: string }
  account: { id: string; name: string; contract_pdf_url?: string | null }
  contractPdfUrl?: string
}

interface ConversionResponse {
  data: ConversionData
}

interface QuotationResponse {
  quotation: QuotationRow
}

// ── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of quotations with optional filters.
 * Role-based visibility is handled server-side.
 */
export function useQuotations(filters?: QuotationListFilters) {
  // Build query string from filters
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.limit) params.set('limit', String(filters.limit))
  if (filters?.offset) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const path = `/api/v1/quotations${qs ? `?${qs}` : ''}`

  return useQuery<QuotationRow[]>({
    queryKey: quotationKeys.list(filters),
    queryFn: async () => {
      const res = await apiGet<QuotationsResponse>(path)
      return res.quotations
    },
  })
}

/**
 * Fetch a single quotation by ID.
 */
export function useQuotation(id: string) {
  return useQuery<QuotationRow>({
    queryKey: quotationKeys.detail(id),
    queryFn: async () => {
      const res = await apiGet<QuotationResponse>(`/api/v1/quotations/${id}`)
      return res.quotation
    },
    enabled: !!id,
  })
}

/**
 * Mutation to create a new quotation.
 * Invalidates the quotations list cache on success.
 */
export function useCreateQuotation() {
  const queryClient = useQueryClient()

  return useMutation<QuotationRow, Error, CreateQuotationDTO>({
    mutationFn: async (data) => {
      const res = await apiPost<QuotationResponse>('/api/v1/quotations', data)
      return res.quotation
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.all })
    },
  })
}

/**
 * Mutation to advance a quotation's status through the state machine.
 * Invalidates both the list and the individual detail cache on success.
 */
export function useAdvanceQuotationStatus() {
  const queryClient = useQueryClient()

  return useMutation<QuotationRow, Error, { id: string; status: QuotationStatus }>({
    mutationFn: async ({ id, status }) => {
      const res = await apiPut<QuotationResponse>(`/api/v1/quotations/${id}/status`, { status })
      return res.quotation
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.all })
      queryClient.invalidateQueries({ queryKey: quotationKeys.detail(variables.id) })
    },
  })
}

/**
 * Mutation to convert an accepted quotation to a Client + Account.
 *
 * This is the core revenue action. The conversion is atomic server-side:
 * if either the Client or Account INSERT fails, both are rolled back.
 *
 * On success:
 * - Invalidates the quotation cache (client_id is now set)
 * - Returns the newly created client and account IDs
 */
export function useConvertQuotation() {
  const queryClient = useQueryClient()

  return useMutation<ConversionData, Error, string>({
    mutationFn: async (quotationId) => {
      const res = await apiPost<ConversionResponse>(
        `/api/v1/quotations/${quotationId}/convert`,
        {},
      )
      return res.data
    },
    onSuccess: (_data, quotationId) => {
      // Invalidate the quotation so client_id is visible in the UI
      queryClient.invalidateQueries({ queryKey: quotationKeys.detail(quotationId) })
      queryClient.invalidateQueries({ queryKey: quotationKeys.all })
    },
  })
}
